// src/pages/user-pages/InvoicesManagement.js
import { useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

// Assets
import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import infoIcon from "../../assets/information.png";

// Contexts and Utilities
import { DataContext } from "../../utils/DataContext";
import { UserAuth } from "../../utils/AuthContext";
import { sanitizeNumber } from "../../utils/sanitizeNumber";
import useRequest from "../../utils/useRequest";
import { formatDate } from "../../utils/toDate";
import { toMoney } from "../../utils/toMoney";
import useLanguage from "../../utils/useLanguage";

// Components
import BackToDashboard from "../../components/BackToDashboard";
import Loader from "../../components/Loader";
import AddAppointmentModal from "./AddAppointmentModal";
import RoundedTable from "../../components/table-component/RoundedTable";
import { TABLE_CELL_TYPES } from "../../components/table-component/RoundedTable";
import CustomButton from "../../components/CustomButton";
import InformationBox from "../../components/InformationBox";
import MultipleSearchFilter from "../../components/MultipleSearchFilter";
import BalanceBoxes from "../../components/BalanceBoxes";
import EditAppointmentModal from "./EditAppointmentModal";

// Styles
import "../InvoicesManagement.scss";

const PatientProfile = () => {
  // ==============================
  // 1. Contexts and Translations
  // ==============================
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { id } = useParams();
  const { currentDirection } = useLanguage();
  const {
    loading,
    setLoading,
    searchQuery,
    setSearchQuery,
    searchError,
    searchErrorMessage,
  } = useContext(DataContext);

  // API Hooks
  const { data: treatments, refetch: getAllMedicalTreatments } = useRequest(
    "/getAllMedicalTreatments",
    "GET",
    {},
    { organizationId: userData?.organizationId },
    false
  );

  const getAllDoctors = async () => {
    const token = localStorage.getItem("token");
    const userId = parseInt(localStorage.getItem("userId"), 10);
    const organizationId = userData?.organizationId;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getUsers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": userId,
            "organization-Id": organizationId,
          },
          params: {
            page: 1,
            limit: 10,
            search: "",
            // role: "Doctor",
          },
        }
      );

      if (response?.status === 200) {
        const doctors = response.data.users
          .filter((user) => user?.role === "Doctor" || user?.role === "Healer")
          .map((user) => ({
            id: user.id,
            name: user.username || user.email,
            email: user.email,
            role: user.role,
            specialty: user.specialty,
          }));

        setAllDoctors([{ name: "All", id: "All" }, ...doctors]);
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  // ==============================
  // 2. State Management
  // ==============================
  // Appointments Data
  const [appointments, setAppointments] = useState([]);
  const [totalAmount, setTotalAmount] = useState(null);
  const [appointmentsCurrentPage, setAppointmentsCurrentPage] = useState(1);
  const [appointmentsTotalPages, setAppointmentsTotalPages] = useState(0);
  const [appointmentsTotalRecords, setAppointmentsTotalRecords] = useState(0);
  const [newAppointmentAdded, setNewAppointmentAdded] = useState(false);
  const [customer, setCustomer] = useState(null);

  // Search and Filters
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("price"),
    value: "price",
  });

  // Modals and UI
  const [showInputFields, setShowInputFields] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);

  // Appointment Editing
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [tempAppointment, setTempAppointment] = useState(null);
  const [editAppointment, setEditAppointment] = useState(false);
  const [errorFields, setErrorFields] = useState({});

  const [allDoctors, setAllDoctors] = useState([{ name: "All", id: "All" }]);

  // ==============================
  // 3. Constants and Configurations
  // ==============================
  const titles = useMemo(
    () => [
      { value: "patientName" },
      { value: "appointmentType" },
      { value: "Date" },
      { value: "time" },
      { value: "price" },
      { value: "healerName" },
      { value: "theOptions" },
    ],
    []
  );

  const searchTitles = useMemo(
    () => [
      { title: t("price"), value: "price", type: "money" },
      {
        title: t("treatmentType"),
        value: "treatment",
        type: "dropdown",
        dropdown: {
          data: treatments?.data || [],
        },
      },
    ],
    [t, treatments]
  );

  // ==============================
  // 4. API Functions
  // ==============================
  const getAllAppointments = async (page = 1, limit = 5, searchTerm = "") => {
    try {
      setLoading(true);
      const token = userData?.token;
      const organizationId = userData?.organizationId;

      if (!token) {
        console.error("Token is missing, aborting request.");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllPatientAppointments/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            page: page || 1,
            limit: limit,
            searchType: selectedTitle?.value,
            searchQuery: searchTerm,
            organizationId: parseInt(organizationId),
            startDate,
            endDate,
          },
        }
      );

      if (response.status === 200) {
        setTotalAmount(response?.data?.totalPrice);
        const incomingAppointments = response.data.data.map((appointment) => [
          { type: TABLE_CELL_TYPES.TEXT, value: appointment.customerName },
          {
            type: TABLE_CELL_TYPES.TEXT,
            value: appointment.treatmentName || "-",
          },
          { type: TABLE_CELL_TYPES.DATE, value: appointment.date },
          { type: TABLE_CELL_TYPES.TEXT, value: appointment.time },
          {
            type: TABLE_CELL_TYPES.MONEY,
            value: appointment.priceAfterDiscount ?? appointment.price ?? 0,
            currency: t("IQD"),
          },
          { type: TABLE_CELL_TYPES.TEXT, value: appointment.doctorName },
          {
            type: TABLE_CELL_TYPES.ELEMENT,
            value: (
              <CustomButton
                variant="blank"
                title={t("ViewInfo")}
                onClick={() => handleViewInfoClick(appointment)}
                icon={infoIcon}
                iconSize={15}
              />
            ),
          },
        ]);

        setAppointments(incomingAppointments);
        setCustomer(response?.data?.patient);
        setAppointmentsTotalPages(response?.data?.pagination?.pages);
        setAppointmentsTotalRecords(response?.data?.pagination?.total);
      }
    } catch (error) {
      console.error(
        "Error fetching appointments:",
        error.response?.data || error
      );
    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // 5. Event Handlers
  // ==============================
  const handleSearchType = (type) => {
    if (type.value === "price" || type.value === "priceAfterDiscount") {
      setSearchQuery("");
    } else if (type.value === "treatment") {
      getAllMedicalTreatments();
    } else {
      setStartDate(formatDate(new Date()));
      setEndDate(formatDate(new Date()));
    }
    setSelectedTitle(type);
  };

  const handleSearch = () => {
    let finalSearchQuery;

    if (
      selectedTitle?.value === "price" ||
      selectedTitle?.value === "priceAfterDiscount"
    ) {
      finalSearchQuery = sanitizeNumber(searchQuery);
    } else if (selectedTitle?.value === "treatment") {
      finalSearchQuery = searchQuery?.name;
    } else if (selectedTitle?.value === "date") {
      finalSearchQuery = { from: startDate, to: endDate };
    } else {
      finalSearchQuery = searchQuery;
    }

    getAllAppointments(appointmentsCurrentPage, 5, finalSearchQuery);
  };

  const handleViewInfoClick = (appointmentData) => {
    setSelectedAppointment(appointmentData);
    setTempAppointment(appointmentData);
    setShowInfoModal(true);
  };

  const handleShowInputFields = () => {
    setShowInputFields(!showInputFields);
  };

  // ==============================
  // 6. Validation Functions
  // ==============================
  const validateEditFields = () => {
    let errors = {};
    let hasError = false;

    if (!tempAppointment?.treatmentName?.trim()) {
      errors.treatmentName = t("medicalTreatmentRequired");
      hasError = true;
    }
    if (!tempAppointment?.date) {
      errors.date = t("validDateRequired");
      hasError = true;
    }
    if (!tempAppointment?.time) {
      errors.time = t("validTimeRequired");
      hasError = true;
    }
    if (
      !tempAppointment?.price ||
      isNaN(tempAppointment.price) ||
      parseFloat(tempAppointment.price) < 0
    ) {
      errors.price = t("validPriceRequired");
      hasError = true;
    }
    if (!tempAppointment?.doctorName?.trim()) {
      errors.doctorName = t("doctorNameRequired");
      hasError = true;
    }
    if (!tempAppointment?.notes?.trim()) {
      errors.notes = t("notesRequired");
      hasError = true;
    }

    setErrorFields(errors);
    return !hasError;
  };

  // ! DON'T REMOVE. Use the below function to update state without req
  const handleConfirmEditAppointment = (response) => {
    const appointment = response?.data;

    const readyToAddAppointment = [
      { type: TABLE_CELL_TYPES.TEXT, value: appointment.customerName },
      {
        type: TABLE_CELL_TYPES.TEXT,
        value: appointment.treatmentName || "-",
      },
      { type: TABLE_CELL_TYPES.DATE, value: appointment.date },
      { type: TABLE_CELL_TYPES.TEXT, value: appointment.time },
      {
        type: TABLE_CELL_TYPES.MONEY,
        value:
          appointment.price ??
          appointment.medicalTreatment?.treatmentPrice ??
          0,
      },
      { type: TABLE_CELL_TYPES.TEXT, value: appointment.doctorName },
      {
        type: TABLE_CELL_TYPES.ELEMENT,
        value: (
          <CustomButton
            variant="blank"
            title={t("ViewInfo")}
            onClick={() => handleViewInfoClick(appointment)}
            icon={infoIcon}
            iconSize={15}
          />
        ),
      },
    ];

    setAppointments((prev) => {
      const prevAppointments = prev?.filter(
        (appointment) => appointment?.id !== readyToAddAppointment?.id
      );

      return [...prevAppointments, readyToAddAppointment];
    });
  };

  // ==============================
  // 7. Side Effects
  // ==============================
  useEffect(() => {
    getAllAppointments(appointmentsCurrentPage, 5);
    getAllDoctors();
  }, [newAppointmentAdded, appointmentsCurrentPage]);

  useEffect(() => {
    if (selectedTitle?.value === "treatment" && treatments?.data) {
      setSelectedTitle((prev) => ({
        ...prev,
        dropdown: {
          ...prev.dropdown,
          data: treatments.data,
        },
      }));
    }
  }, [selectedTitle?.value, treatments]);

  // ==============================
  // 8. Render
  // ==============================
  return (
    <>
      <main className="e-container">
        <div>
          <BackToDashboard
            title={t("back")}
            variant="blank"
            icon={currentDirection === "ltr" ? previousIcon : nextIcon}
            iconSize={14}
            to={
              userData?.userType === "user" ? "/invoiceManagement" : "/clinic-dashboard"
            }
          />
        </div>

        <div className="invoice-table-container">
          <div className="user-section">
            <div className="user">
              <span className="user-text">{`${t("user")}:`}</span>
              <span className="user-text">
                {localStorage.getItem("username")}
              </span>
            </div>
          </div>

          <div className="under-user-section">
            <div className="page-title">
              {t("customerAppointments")} {customer?.name}
            </div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              <MultipleSearchFilter
                options={searchTitles}
                selected={selectedTitle}
                setSelected={handleSearchType}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                thick
                getData={getAllAppointments}
                handleSearch={handleSearch}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                startDate={startDate}
                endDate={endDate}
              />
              {appointments.length > 0 ? (
                <>
                  <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end u-margin--bottom_400"></section>

                  <RoundedTable titles={titles} rows={appointments} />

                  <section className="pagination">
                    <div className="pagination-buttons">
                      <CustomButton
                        variant="blank"
                        title={t("previous")}
                        onClick={() =>
                          setAppointmentsCurrentPage(
                            appointmentsCurrentPage - 1
                          )
                        }
                        disabled={appointmentsCurrentPage === 1}
                        icon={
                          currentDirection === "ltr" ? previousIcon : nextIcon
                        }
                        iconSize={15}
                      />
                      <span className="pagination-location">
                        {t("page")}: {appointmentsCurrentPage} -{" "}
                        {appointmentsTotalPages}
                      </span>
                      <CustomButton
                        title={t("next")}
                        variant="blank"
                        onClick={() =>
                          setAppointmentsCurrentPage(
                            appointmentsCurrentPage + 1
                          )
                        }
                        disabled={
                          appointmentsCurrentPage === appointmentsTotalPages
                        }
                        icon={
                          currentDirection === "ltr" ? nextIcon : previousIcon
                        }
                        iconSize={15}
                        reverseIcon
                      />
                    </div>

                    <div className="pagination-buttons">
                      <span className="pagination-location">
                        {t("totalRecords")}: {appointmentsTotalRecords}
                      </span>
                    </div>
                  </section>

                  <div>
                    <BalanceBoxes
                      boxes={[
                        {
                          title: "totalAmount",
                          value: `${toMoney(totalAmount)} ${t("IQD")}`,
                        },
                      ]}
                    />
                  </div>
                </>
              ) : (
                <InformationBox>
                  {searchError
                    ? t(searchErrorMessage)
                    : t("noAppointmentFound")}
                </InformationBox>
              )}
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      {showInputFields && (
        <AddAppointmentModal
          handleShowAddAppointment={handleShowInputFields}
          handleNewAppointmentAdded={setNewAppointmentAdded}
          setShowModal={setShowInputFields}
        />
      )}

      {showInfoModal && (
        <EditAppointmentModal
          appointment={selectedAppointment}
          setShowModal={setShowInfoModal}
          allDoctors={allDoctors?.filter((doctor) => doctor.id !== "All")}
          getAllAppointments={getAllAppointments}
          // onConfirm={handleConfirmEditAppointment}
        />
      )}
    </>
  );
};

export default PatientProfile;
